import MailIcon from '@/assets/icons/mail.svg?react';
import { useCountdown } from '@/shared/hooks/use-countdown';
import { formatCountdown } from '@/shared/lib/format';
import { BadgeIcon } from '@/shared/ui/badge-icon';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { useResendSignInLink } from '../hooks/use-resend-sign-in-link';
import type { SignInLinkData } from '../types';

type EmailSentViewProps = {
  link: SignInLinkData;
  isResent: boolean;
  onResent: (link: SignInLinkData) => void;
  onUseDifferentEmail: () => void;
};

export function EmailSentView({
  link,
  isResent,
  onResent,
  onUseDifferentEmail,
}: EmailSentViewProps) {
  const secondsLeft = useCountdown(link.resendAvailableAt);
  const { resendSignInLink, isResending } = useResendSignInLink();
  const isCooling = secondsLeft > 0;

  const resend = async () => {
    const outcome = await resendSignInLink(link.id);
    if (outcome.link) onResent(outcome.link);
  };

  return (
    <>
      <span data-enter="">
        <BadgeIcon tone="ok">
          <MailIcon className="size-5.5" />
        </BadgeIcon>
      </span>
      <div data-enter="" className="flex flex-col gap-2">
        <h1 className="font-semibold text-title-lg">Check your email</h1>
        <p className="text-muted">
          We sent a sign-in link to <span className="font-medium text-ink">{link.email}</span>. It
          works once, for 15 minutes.
        </p>
      </div>
      {isResent ? (
        <div data-enter="" role="status">
          <Banner tone="ok" size="small">
            A new link is on its way. Links sent earlier no longer work.
          </Banner>
        </div>
      ) : null}
      <Button
        data-enter=""
        size="lg"
        isBlock
        disabled={isCooling}
        isLoading={isResending}
        onClick={() => void resend()}
      >
        {isCooling ? `Resend in ${formatCountdown(secondsLeft)}` : 'Resend the link'}
      </Button>
      <p data-enter="" className="text-meta text-muted">
        Wrong address?{' '}
        <Button variant="link" size="inline" onClick={onUseDifferentEmail}>
          Use a different email
        </Button>
      </p>
    </>
  );
}
